import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Shield, Plus, Power, LogOut, KeyRound } from "lucide-react";

import styles from "./team.module.scss";
import Spinner from "../../components/spinner";
import { usePermissions } from "../../context/permissionContext";
import AdminFormModal from "./adminFormModal";
import SessionsModal from "./sessionsModal";

const API = `${process.env.REACT_APP_API_ROOT}/api/admin`;

const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return { Authorization: `Bearer ${user.token}` };
};

/**
 * Team & access (owner only): who can sign in, what each person may do, and
 * which devices they are signed in on.
 */
function Team() {
  const { admin: currentAdmin, can, refresh: refreshMe } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [catalog, setCatalog] = useState({ features: [], actions: [], roles: [] });

  const [editing, setEditing] = useState(null); // admin being edited, or "new"
  const [sessionsFor, setSessionsFor] = useState(null);

  const canEdit = can("admins", "edit");
  const canCreate = can("admins", "create");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [adminsRes, catalogRes] = await Promise.all([
        axios.get(`${API}/admins`, { headers: authHeaders() }),
        axios.get(`${API}/access/catalog`, { headers: authHeaders() }),
      ]);
      setAdmins(adminsRes.data?.data || []);
      setCatalog(catalogRes.data?.data || { features: [], actions: [], roles: [] });
    } catch (error) {
      toast.error(error.response?.data?.error || "Could not load the team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roleLabels = useMemo(
    () => Object.fromEntries((catalog.roles || []).map((r) => [r.key, r.label])),
    [catalog.roles]
  );

  const handleToggleActive = async (target) => {
    const nextActive = !target.isActive;
    const verb = nextActive ? "activate" : "deactivate";
    if (!window.confirm(`Are you sure you want to ${verb} ${target.userName}?`)) return;

    try {
      await axios.put(
        `${API}/admins/${target._id}/status`,
        { isActive: nextActive },
        { headers: authHeaders() }
      );
      toast.success(`${target.userName} ${nextActive ? "activated" : "deactivated"}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || `Could not ${verb} the account`);
    }
  };

  const handleLogoutEverywhere = async (target) => {
    if (
      !window.confirm(
        `Sign ${target.userName} out of all devices? They will need to log in again.`
      )
    ) {
      return;
    }

    try {
      await axios.post(
        `${API}/admins/${target._id}/sessions/revoke-all`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`${target.userName} signed out everywhere`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Could not revoke sessions");
    }
  };

  const handleSaved = () => {
    setEditing(null);
    load();
    // The owner may have changed their own name; keep the header in step.
    refreshMe();
  };

  if (loading) return <Spinner />;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>
            <Shield size={22} /> Team &amp; Access
          </h2>
          <p className={styles.subtitle}>
            Give each person their own login and control exactly which sections
            they can reach. Listers can add and edit content but never delete it.
          </p>
        </div>

        {/* Plain button, not the shared <Button>: that component renders a
            `text` prop rather than children, so this came out blank. */}
        {canCreate && (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setEditing("new")}
          >
            <Plus size={16} /> Add member
          </button>
        )}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Access</th>
              <th>Last login</th>
              <th>Status</th>
              <th className={styles.actionsCol}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => {
              const isSelf = String(a._id) === String(currentAdmin?._id);
              const featureCount = Object.values(a.permissions || {}).filter(
                (actions) => actions.length > 0
              ).length;

              return (
                <tr key={a._id} className={a.isActive ? "" : styles.inactiveRow}>
                  <td>
                    <span className={styles.name}>{a.userName}</span>
                    {isSelf && <span className={styles.youTag}>you</span>}
                  </td>
                  <td className={styles.muted}>{a.email}</td>
                  <td>
                    <span className={`${styles.roleTag} ${styles[a.role] || ""}`}>
                      {roleLabels[a.role] || a.role}
                    </span>
                  </td>
                  <td className={styles.muted}>
                    {featureCount} section{featureCount === 1 ? "" : "s"}
                    {a.hasOverrides && (
                      <span className={styles.customTag}>custom</span>
                    )}
                  </td>
                  <td className={styles.muted}>
                    {a.lastLoginAt
                      ? new Date(a.lastLoginAt).toLocaleString()
                      : "Never"}
                  </td>
                  <td>
                    <span
                      className={a.isActive ? styles.active : styles.inactive}
                    >
                      {a.isActive ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className={styles.actionsCol}>
                    {canEdit && (
                      <>
                        <button
                          className={styles.iconBtn}
                          title="Edit role & permissions"
                          onClick={() => setEditing(a)}
                        >
                          <KeyRound size={16} />
                        </button>
                        <button
                          className={styles.iconBtn}
                          title="Devices"
                          onClick={() => setSessionsFor(a)}
                        >
                          <LogOut size={16} />
                        </button>
                        {/* An owner cannot disable themselves — the API blocks
                            it too, this just avoids offering a dead action. */}
                        {!isSelf && (
                          <button
                            className={`${styles.iconBtn} ${
                              a.isActive ? styles.danger : ""
                            }`}
                            title={a.isActive ? "Deactivate" : "Activate"}
                            onClick={() => handleToggleActive(a)}
                          >
                            <Power size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}

            {admins.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No team members yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <AdminFormModal
          admin={editing === "new" ? null : editing}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {sessionsFor && (
        <SessionsModal
          admin={sessionsFor}
          onClose={() => setSessionsFor(null)}
          onRevokedAll={() => {
            setSessionsFor(null);
            handleLogoutEverywhere(sessionsFor);
          }}
        />
      )}
    </div>
  );
}

export default Team;
